import React, { useState, useEffect } from 'react';
import { getIntl, saveIntl } from '../services/businessIntl';

/*
 * BusinessIntlSettings (בינלאומי) — the business's international settings: currency, timezone and
 * default language. Owner-only (rendered under an ownerOnly nav item in SettingsModal). Loads via
 * getIntl() on mount, saves via saveIntl(). The SERVER strictly whitelists every value, so the
 * selects here just mirror that whitelist. Reuses the settings save UX + styles (form-section /
 * save-btn from settingmodal.css).
 */

// Mirror the server-side whitelists (php/act.php → updateBusinessIntl).
const CURRENCIES = [
    { value: 'ILS', label: 'שקל ₪ (ILS)' },
    { value: 'USD', label: 'דולר $ (USD)' },
    { value: 'EUR', label: 'אירו € (EUR)' },
    { value: 'GBP', label: 'לירה שטרלינג £ (GBP)' },
];

const TIMEZONES = [
    { value: '', label: 'לא מוגדר' },
    { value: 'Asia/Jerusalem', label: 'ירושלים (Asia/Jerusalem)' },
    { value: 'America/New_York', label: 'ניו יורק (America/New_York)' },
    { value: 'America/Los_Angeles', label: 'לוס אנג׳לס (America/Los_Angeles)' },
    { value: 'America/Chicago', label: 'שיקגו (America/Chicago)' },
    { value: 'Europe/London', label: 'לונדון (Europe/London)' },
    { value: 'Europe/Paris', label: 'פריז (Europe/Paris)' },
    { value: 'Europe/Berlin', label: 'ברלין (Europe/Berlin)' },
    { value: 'Europe/Madrid', label: 'מדריד (Europe/Madrid)' },
];

const LOCALES = [
    { value: 'he', label: 'עברית' },
    { value: 'en', label: 'English' },
];

const BusinessIntlSettings = () => {
    const [currency, setCurrency] = useState('ILS');
    const [timezone, setTimezone] = useState('');
    const [defaultLocale, setDefaultLocale] = useState('he');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(''); // transient save feedback

    // Load the real settings on mount.
    useEffect(() => {
        let alive = true;
        (async () => {
            const intl = await getIntl();
            if (!alive) return;
            setCurrency(intl.currency);
            setTimezone(intl.timezone);
            setDefaultLocale(intl.defaultLocale);
            setLoading(false);
        })();
        return () => { alive = false; };
    }, []);

    // Auto-clear the transient save feedback.
    useEffect(() => {
        if (!status) return undefined;
        const t = setTimeout(() => setStatus(''), 2600);
        return () => clearTimeout(t);
    }, [status]);

    const handleSave = async () => {
        if (saving || loading) return;
        setSaving(true);
        const res = await saveIntl({ currency, timezone, defaultLocale });
        setSaving(false);
        if (res.ok && res.intl) {
            // Reflect the server's persisted (whitelisted) values.
            setCurrency(res.intl.currency);
            setTimezone(res.intl.timezone);
            setDefaultLocale(res.intl.defaultLocale);
            setStatus('נשמר');
        } else {
            setStatus('שמירה נכשלה — נסו שוב');
        }
    };

    return (
        <div>
            <h3 className='seat-title'>הגדרות בינלאומיות</h3>

            <div className='form-section'>
                <label htmlFor='intl-currency'>מטבע</label>
                <select id='intl-currency' value={currency}
                    onChange={(e) => setCurrency(e.target.value)} disabled={loading || saving}>
                    {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
            </div>

            <div className='form-section'>
                <label htmlFor='intl-timezone'>אזור זמן</label>
                <select id='intl-timezone' value={timezone}
                    onChange={(e) => setTimezone(e.target.value)} disabled={loading || saving}>
                    {TIMEZONES.map(t => <option key={t.value || 'none'} value={t.value}>{t.label}</option>)}
                </select>
            </div>

            <div className='form-section'>
                <label htmlFor='intl-locale'>שפת ברירת מחדל</label>
                <select id='intl-locale' value={defaultLocale}
                    onChange={(e) => setDefaultLocale(e.target.value)} disabled={loading || saving}>
                    {LOCALES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
            </div>

            <div className='form-section' style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)' }}>
                <button type='button' className='save-btn' onClick={handleSave} disabled={loading || saving}>
                    {saving ? 'שומר…' : 'שמירה'}
                </button>
                {status && (
                    <span role='status' aria-live='polite'
                        style={{ fontSize: 'var(--fs-sm)', color: status === 'נשמר' ? 'var(--success)' : 'var(--danger)' }}>
                        {status}
                    </span>
                )}
            </div>
        </div>
    );
};

export default BusinessIntlSettings;
